import React from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import LockoutNotice from './LockoutNotice';
import { useLoginForm } from './useLoginForm';

const challengePlaceholderKey = (
  challengeType: 'SMS_MFA' | 'SOFTWARE_TOKEN_MFA' | null
) => {
  if (challengeType === 'SMS_MFA') return 'auth.mfa.sms_placeholder';
  return 'auth.mfa.totp_placeholder';
};

const LoginForm: React.FC = () => {
  const {
    showPassword,
    setShowPassword,
    loading,
    email,
    setEmail,
    password,
    setPassword,
    challengeCode,
    setChallengeCode,
    error,
    lockout,
    step,
    challengeType,
    isLocked,
    mfaResetStatus,
    handleSubmit,
    handleBackToLogin,
    handleLockoutExpire,
    handleRequestMfaReset,
    t,
  } = useLoginForm();

  const isChallengeStep = step === 'challenge';
  const mfaResetRequested =
    mfaResetStatus === 'sent' || mfaResetStatus === 'already_pending';

  return (
    <div className="w-full max-w-md mx-auto p-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-gray-700 font-medium">
            {t('auth.email')}
          </Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={event => setEmail(event.target.value)}
            className="bg-input border-0 rounded-lg h-12 px-4"
            placeholder={t('auth.email_placeholder')}
            disabled={isChallengeStep || isLocked}
          />
        </div>

        {!isChallengeStep && (
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label htmlFor="password" className="text-gray-700 font-medium">
                {t('auth.password')}
              </Label>
              <Link
                to="/forgot-password"
                className="text-sm text-blue-700 hover:underline"
              >
                {t('auth.forgot_password')}
              </Link>
            </div>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={event => setPassword(event.target.value)}
                className="bg-input border-0 rounded-lg h-12 px-4 pr-20"
                placeholder={t('auth.password_placeholder')}
                disabled={isLocked}
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-white border border-gray-200 rounded flex items-center justify-center"
                onClick={() => setShowPassword(current => !current)}
                aria-label={
                  showPassword
                    ? t('auth.hide_password')
                    : t('auth.show_password')
                }
              >
                {showPassword ? (
                  <EyeOff className="w-3 h-3 text-black" />
                ) : (
                  <Eye className="w-3 h-3 text-black" />
                )}
              </button>
            </div>
          </div>
        )}

        {isChallengeStep && !mfaResetRequested && (
          <div className="space-y-2">
            <Label
              htmlFor="challengeCode"
              className="text-gray-700 font-medium"
            >
              {t('auth.mfa.code')}
            </Label>
            <Input
              id="challengeCode"
              type="text"
              value={challengeCode}
              onChange={event => setChallengeCode(event.target.value)}
              className="bg-input border-0 rounded-lg h-12 px-4"
              placeholder={t(challengePlaceholderKey(challengeType))}
            />
            <button
              type="button"
              onClick={handleRequestMfaReset}
              disabled={mfaResetStatus === 'sending'}
              className="text-sm text-blue-700 hover:underline disabled:opacity-50"
            >
              {mfaResetStatus === 'sending'
                ? t('auth.mfa.reset_request_sending')
                : t('auth.mfa.lost_device')}
            </button>
            {mfaResetStatus === 'error' && (
              <p className="text-red-500 text-sm">
                {t('auth.mfa.reset_request_error')}
              </p>
            )}
          </div>
        )}

        {isChallengeStep && mfaResetRequested && (
          <div className="rounded-lg bg-gray-50 border border-gray-200 p-4 text-center">
            <p className="text-gray-900 font-semibold mb-2">
              {t('auth.mfa.reset_request_sent_title')}
            </p>
            <p className="text-gray-600 text-sm">
              {mfaResetStatus === 'already_pending'
                ? t('auth.mfa.reset_request_already_pending')
                : t('auth.mfa.reset_request_sent_body')}
            </p>
          </div>
        )}

        {error && <p className="text-red-500 text-center">{error}</p>}

        {lockout && (
          <LockoutNotice
            message={lockout.message}
            lockedUntil={lockout.lockedUntil}
            onExpire={handleLockoutExpire}
          />
        )}

        {isChallengeStep && (
          <Button
            type="button"
            onClick={handleBackToLogin}
            variant="outline"
            className="w-full h-12 border-gray-300 text-gray-700 hover:bg-gray-50 font-semibold rounded-lg text-base"
          >
            {t('auth.mfa.back_to_login')}
          </Button>
        )}

        {!mfaResetRequested && (
          <Button
            type="submit"
            className="w-full h-12 bg-primary hover:bg-primary-600 text-primary-foreground font-semibold rounded-lg text-base"
            disabled={loading || isLocked}
          >
            {isChallengeStep ? t('app.submit') : t('auth.sign_in_button')}
          </Button>
        )}
      </form>
    </div>
  );
};

export default LoginForm;
